import { useState, useEffect, useMemo } from 'react'
import { Search, Filter, X, Mail, TrendingUp, Users, DollarSign, Clock, AlertCircle } from 'lucide-react'
import type { Client } from '@/types'
import type { OnboardingClient, PipelineFilters, PipelineStats } from '@/types/pipeline'
import { mapClientToOnboardingClient, pipelineStages, onboardingSteps } from '@/types/pipeline'

interface OnboardingPipelineViewProps {
  clients: Client[]
  onClientSelect?: (client: Client) => void
}

export function OnboardingPipelineView({ clients, onClientSelect }: OnboardingPipelineViewProps) {
  const [filteredClients, setFilteredClients] = useState<OnboardingClient[]>([])
  const [filters, setFilters] = useState<PipelineFilters>({
    searchText: '',
    stages: [],
    priorities: [],
    tiers: [],
    coaches: [],
    daysInStageRange: null,
    valueRange: null
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedClient, setSelectedClient] = useState<OnboardingClient | null>(null)
  const [showClientModal, setShowClientModal] = useState(false)

  // Convert clients to onboarding clients
  const onboardingClients = useMemo(() => {
    return clients.map(mapClientToOnboardingClient)
  }, [clients])

  // Apply filters
  useEffect(() => {
    let filtered = [...onboardingClients]

    // Search filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase()
      filtered = filtered.filter(client => 
        client.firstName.toLowerCase().includes(searchLower) ||
        client.lastName.toLowerCase().includes(searchLower) ||
        client.email.toLowerCase().includes(searchLower)
      )
    }

    // Stage filter
    if (filters.stages.length > 0) {
      filtered = filtered.filter(client => filters.stages.includes(client.stage))
    }

    // Priority filter
    if (filters.priorities.length > 0) {
      filtered = filtered.filter(client => filters.priorities.includes(client.priority))
    }

    // Tier filter
    if (filters.tiers.length > 0) {
      filtered = filtered.filter(client => filters.tiers.includes(client.tier))
    }

    // Days in stage filter
    if (filters.daysInStageRange) {
      const [min, max] = filters.daysInStageRange
      filtered = filtered.filter(client => 
        client.daysInStage >= min && client.daysInStage <= max
      )
    }

    // Value range filter
    if (filters.valueRange) {
      const [min, max] = filters.valueRange
      filtered = filtered.filter(client => 
        client.value >= min && client.value <= max
      )
    }

    setFilteredClients(filtered)
  }, [onboardingClients, filters])

  // Pipeline statistics
  const stats: PipelineStats = useMemo(() => {
    const totalClients = filteredClients.length
    const totalValue = filteredClients.reduce((sum, client) => sum + client.value, 0)
    const avgDaysInStage = totalClients > 0 
      ? Math.round(filteredClients.reduce((sum, client) => sum + client.daysInStage, 0) / totalClients) 
      : 0
    const activeClients = filteredClients.filter(c => c.stage === 'active').length
    const conversionRate = totalClients > 0 ? Math.round((activeClients / totalClients) * 100) : 0

    const byStage = pipelineStages.reduce((acc, stage) => {
      acc[stage.key] = filteredClients.filter(c => c.stage === stage.key).length
      return acc
    }, {} as Record<string, number>)

    const byPriority = {
      high: filteredClients.filter(c => c.priority === 'high').length,
      medium: filteredClients.filter(c => c.priority === 'medium').length,
      low: filteredClients.filter(c => c.priority === 'low').length
    }

    return {
      totalClients,
      totalValue,
      avgDaysInStage,
      conversionRate,
      byStage,
      byPriority
    }
  }, [filteredClients])

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      searchText: '',
      stages: [],
      priorities: [],
      tiers: [],
      coaches: [],
      daysInStageRange: null,
      valueRange: null
    })
  }

  // Handle client click
  const handleClientClick = (client: OnboardingClient) => {
    setSelectedClient(client)
    setShowClientModal(true)
  }

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-red-500 bg-red-50'
      case 'medium': return 'border-orange-500 bg-orange-50'
      case 'low': return 'border-green-500 bg-green-50'
      default: return 'border-gray-300 bg-gray-50'
    }
  }

  // Get tier badge color
  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'Basic': return 'bg-gray-100 text-gray-800'
      case 'Standard': return 'bg-blue-100 text-blue-800'
      case 'Premium': return 'bg-yellow-100 text-yellow-800'
      case 'Elite': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Onboarding Pipeline</h2>
        <p className="text-gray-600">Track and manage client onboarding process through visual pipeline stages</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients by name or email..."
              value={filters.searchText}
              onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
              className="coach-input pl-9"
            />
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {filteredClients.length} of {onboardingClients.length} prospects
            </span>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`coach-button-secondary px-3 py-2 flex items-center space-x-2 ${showFilters ? 'bg-primary-100 text-primary-600' : ''}`}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
            <button
              onClick={clearFilters}
              className="coach-button-secondary px-3 py-2 flex items-center space-x-2"
              disabled={!filters.searchText && filters.stages.length === 0 && filters.priorities.length === 0}
            >
              <X className="w-4 h-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Stage Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pipeline Stage</label>
                <div className="space-y-2">
                  {pipelineStages.map(stage => (
                    <label key={stage.key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.stages.includes(stage.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, stages: [...prev.stages, stage.key] }))
                          } else {
                            setFilters(prev => ({ ...prev, stages: prev.stages.filter(s => s !== stage.key) }))
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">{stage.title}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <div className="space-y-2">
                  {['high', 'medium', 'low'].map(priority => (
                    <label key={priority} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.priorities.includes(priority)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, priorities: [...prev.priorities, priority] }))
                          } else {
                            setFilters(prev => ({ ...prev, priorities: prev.priorities.filter(p => p !== priority) }))
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm capitalize">{priority}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tier Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Tier</label>
                <div className="space-y-2">
                  {['Basic', 'Standard', 'Premium', 'Elite'].map(tier => (
                    <label key={tier} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.tiers.includes(tier)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, tiers: [...prev.tiers, tier] }))
                          } else {
                            setFilters(prev => ({ ...prev, tiers: prev.tiers.filter(t => t !== tier) }))
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">{tier}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Quick Filters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quick Filters</label>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        daysInStageRange: prev.daysInStageRange?.[0] === 7 ? null : [7, 100]
                      }))
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md border ${
                      filters.daysInStageRange?.[0] === 7 
                        ? 'bg-primary-100 border-primary-300 text-primary-700' 
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    Stalled (7+ days)
                  </button>
                  <button
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        valueRange: prev.valueRange?.[0] === 300 ? null : [300, 1000]
                      }))
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md border ${
                      filters.valueRange?.[0] === 300 
                        ? 'bg-primary-100 border-primary-300 text-primary-700' 
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    High Value ($300+)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalClients}</p>
              <p className="text-sm text-gray-600">Total Prospects</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">${stats.totalValue.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Pipeline Value</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.avgDaysInStage}</p>
              <p className="text-sm text-gray-600">Avg. Days/Stage</p>
            </div>
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.conversionRate}%</p>
              <p className="text-sm text-gray-600">Conversion Rate</p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Stalled Alert */}
      {stats.byStage.stalled > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <span className="text-yellow-800">
                {stats.byStage.stalled} clients are stalled and need attention
              </span>
            </div>
            <button
              onClick={() => setFilters(prev => ({ ...prev, stages: ['stalled'] }))}
              className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
            >
              Review Details
            </button>
          </div>
        </div>
      )}

      {/* Pipeline Board */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
        {pipelineStages.map(stage => {
          const stageClients = filteredClients.filter(c => c.stage === stage.key)
          return (
            <PipelineStageColumn
              key={stage.key}
              stage={{ ...stage, clients: stageClients }}
              onClientClick={handleClientClick}
              getPriorityColor={getPriorityColor}
              getTierBadgeColor={getTierBadgeColor}
            />
          )
        })}
      </div>

      {/* Client Detail Modal */}
      {showClientModal && selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          onClose={() => setShowClientModal(false)}
          onEdit={() => onClientSelect?.(selectedClient.originalClient)}
        />
      )}
    </div>
  )
}

// Pipeline Stage Column Component
interface PipelineStageColumnProps {
  stage: { key: string; title: string; description: string; color: string; icon: string; clients: OnboardingClient[] }
  onClientClick: (client: OnboardingClient) => void
  getPriorityColor: (priority: string) => string
  getTierBadgeColor: (tier: string) => string
}

function PipelineStageColumn({ stage, onClientClick, getPriorityColor, getTierBadgeColor }: PipelineStageColumnProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 min-h-96">
      {/* Stage Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-lg">{stage.icon}</span>
            <h3 className="font-semibold text-gray-900">{stage.title}</h3>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${stage.color}`}>
            {stage.clients.length}
          </span>
        </div>
        <p className="text-sm text-gray-600">{stage.description}</p>
      </div>

      {/* Stage Content */}
      <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
        {stage.clients.map(client => (
          <ClientCard
            key={client.id}
            client={client}
            onClick={() => onClientClick(client)}
            getPriorityColor={getPriorityColor}
            getTierBadgeColor={getTierBadgeColor}
          />
        ))}

        {stage.clients.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No clients in this stage</p>
          </div>
        )}
      </div>
    </div>
  )
}

// Client Card Component
interface ClientCardProps {
  client: OnboardingClient
  onClick: () => void
  getPriorityColor: (priority: string) => string
  getTierBadgeColor: (tier: string) => string
}

function ClientCard({ client, onClick, getPriorityColor, getTierBadgeColor }: ClientCardProps) {
  return (
    <div
      onClick={onClick}
      className={`p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${getPriorityColor(client.priority)}`}
    >
      {/* Client Header */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-900 text-sm">
          {client.firstName} {client.lastName}
        </h4>
        <span className={`px-2 py-1 rounded-md text-xs font-medium ${getTierBadgeColor(client.tier)}`}>
          {client.tier}
        </span>
      </div>

      {/* Email */}
      <div className="flex items-center space-x-1 mb-2">
        <Mail className="w-3 h-3 text-gray-400" />
        <span className="text-xs text-gray-600 truncate">{client.email}</span>
      </div>

      {/* Progress */}
      <div className="mb-2">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-600">Progress</span>
          <span className="font-medium">{client.progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div 
            className="bg-blue-500 h-1.5 rounded-full" 
            style={{ width: `${client.progress}%` }}
          />
        </div>
      </div>

      {/* Value and Days */}
      <div className="flex justify-between items-center text-xs">
        <span className="font-medium text-green-600">${client.value}</span>
        <span className={`px-2 py-1 rounded-md font-medium ${
          client.daysInStage > 7 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {client.daysInStage}d
        </span>
      </div>
    </div>
  )
}

// Client Detail Modal Component
interface ClientDetailModalProps {
  client: OnboardingClient
  onClose: () => void
  onEdit: () => void
}

function ClientDetailModal({ client, onClose, onEdit }: ClientDetailModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {client.firstName} {client.lastName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Client Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <p className="text-gray-900">{client.email}</p>
            </div>
            {client.phone && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <p className="text-gray-900">{client.phone}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Tier</label>
              <span className={`inline-block px-2 py-1 rounded-md text-sm font-medium ${getTierBadgeColor(client.tier)}`}>
                {client.tier}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Value</label>
              <p className="text-gray-900 font-semibold">${client.value}</p>
            </div>
          </div>

          {/* Progress Steps */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Onboarding Progress</label>
            <div className="space-y-2">
              {onboardingSteps.map((step, index) => {
                const isCompleted = client.completedSteps.includes(step)
                const isCurrent = index === client.completedSteps.length
                return (
                  <div key={step} className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                      isCompleted 
                        ? 'bg-green-500 text-white' 
                        : isCurrent 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-200'
                    }`}>
                      {isCompleted && <span className="text-xs">✓</span>}
                    </div>
                    <span className={`text-sm ${isCompleted ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                      {step}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <p className="text-gray-900 bg-gray-50 p-3 rounded-md">{client.notes}</p>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="coach-button-secondary px-4 py-2"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="coach-button-primary px-4 py-2"
          >
            View Full Details
          </button>
        </div>
      </div>
    </div>
  )
}

// Helper function for tier badge color (local version)
function getTierBadgeColor(tier: string) {
  switch (tier) {
    case 'Basic': return 'bg-gray-100 text-gray-800'
    case 'Standard': return 'bg-blue-100 text-blue-800'
    case 'Premium': return 'bg-yellow-100 text-yellow-800'
    case 'Elite': return 'bg-red-100 text-red-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}